import { Box, Button, Chip, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material';
import { Eye, FileUp, Search, ShoppingCart, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobilePurchasesList from '../../component/purchase/MobilePurchasesList';

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const TB_FIELD_RADIUS = '9px';

// Rótulo fixo (`tb-field`), mesmo padrão de `sales/List.js`/`service_quotes/List.js`.
const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

// `from`/`to` ficam como string YYYY-MM-DD (formato enviado à API); '' = sem limite.
const toDayjs = (value) => (value ? dayjs(value) : null);
const fromDayjs = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');

export default function List() {
    const navigate = useNavigate();
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();

    const [purchases, setPurchases] = useState([]);
    const [reloadKey, setReloadKey] = useState(0);
    const [purchaseToCancel, setPurchaseToCancel] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [loading, setLoading] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        if (from) params.from = from;
        if (to) params.to = to;
        httpClient.get('/purchases', { params })
            .then((response) => setPurchases(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                console.error('Error fetching purchases:', error);
                setPurchases([]);
                setSnackbarMessage('Erro ao carregar compras.');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            })
            .finally(() => setLoading(false));
    }, [debouncedSearch, from, to, reloadKey]);

    const handleClearPeriod = () => {
        setFrom('');
        setTo('');
    };

    // Zera busca (inclusive o valor já com debounce) e período; a lista recarrega sozinha.
    const handleClearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        handleClearPeriod();
    };

    const confirmCancel = async () => {
        try {
            setCanceling(true);
            const response = await httpClient.post(`/purchases/${encodeURIComponent(purchaseToCancel.reference)}/cancel`);
            const warning = response?.data?.warning;
            setPurchaseToCancel(null);
            setReloadKey((k) => k + 1);
            setSnackbarMessage(warning || 'Compra cancelada e estoque estornado com sucesso!');
            setSnackbarSeverity(warning ? 'warning' : 'success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error canceling purchase:', error);
            const data = error?.response?.data;
            setSnackbarMessage(typeof data === 'string' && data.trim() ? data : 'Erro ao cancelar compra.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            setPurchaseToCancel(null);
        } finally {
            setCanceling(false);
        }
    };

    const columns = [
        {
            path: 'date',
            name: 'Data',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatDateTime(row.date)}</Typography>
            ),
        },
        {
            path: 'number',
            name: 'Nota (nº / série)',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                    {row.number ? `${row.number}${row.series ? ` / ${row.series}` : ''}` : '—'}
                </Typography>
            ),
        },
        {
            path: 'supplier',
            name: 'Fornecedor',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: row.supplier ? 'text.primary' : 'text.disabled' }}>
                    {row.supplier || '—'}
                </Typography>
            ),
        },
        {
            path: 'items_count',
            name: 'Itens',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.items_count} produto(s) · {row.total_quantity} un.
                </Typography>
            ),
        },
        {
            path: 'total_cents',
            name: 'Valor total',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.total_cents == null ? 'text.disabled' : 'text.primary' }}>
                    {row.total_cents == null ? '—' : (row.total_cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
            ),
        },
    ];

    const openPurchase = (row) => navigate(`/purchases/${encodeURIComponent(row.reference)}`);
    const actions = [{
        icon: <Eye size={16} strokeWidth={2} />,
        props: {
            onClick: openPurchase,
            tooltip: 'Visualizar',
            style: { cursor: 'pointer' },
        },
    }];

    const canImport = hasPermission('purchase.create');
    if (canImport) {
        actions.push({
            icon: <XCircle size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => setPurchaseToCancel(row),
                tooltip: 'Cancelar compra',
                style: { cursor: 'pointer' },
            },
        });
    }

    const dialog = (
        <DialogForm
            iconType="warning"
            open={Boolean(purchaseToCancel)}
            title="Atenção"
            confirmButtonText="Sim, Cancelar compra"
            cancelButtonText="Voltar"
            content={`Tem certeza que deseja cancelar a compra${purchaseToCancel?.number ? ` da NF ${purchaseToCancel.number}` : ''}?`}
            secondaryContent="Os produtos lançados nesta compra serão retirados do estoque. Se algum já tiver sido vendido ou consumido, o cancelamento não será feito."
            onSubmit={confirmCancel}
            onClose={() => setPurchaseToCancel(null)}
            loading={canceling}
            loadingText="Cancelando..."
        />
    );

    // Shell mobile: lista do protótipo `compras/lista.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Compras"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobilePurchasesList
                    purchases={purchases}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    from={from}
                    to={to}
                    onFromChange={setFrom}
                    onToChange={setTo}
                    canCreate={canImport}
                    canCancel={canImport}
                    onOpen={openPurchase}
                    onCancel={(row) => setPurchaseToCancel(row)}
                    onManual={() => navigate('/purchases/new')}
                    onImport={() => navigate('/purchases/import')}
                />
                {dialog}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Compras"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <ShoppingCart size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Compras
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Notas fiscais de compra já lançadas no estoque.
                            </Typography>
                        </Box>
                    </Box>

                    {canImport && (
                        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                startIcon={<FileUp size={16} strokeWidth={2.5} />}
                                onClick={() => navigate('/purchases/import')}
                            >
                                Importar NF-e
                            </Button>
                            <NewItemButton label="Registrar compra" onClick={() => navigate('/purchases/new')} />
                        </Box>
                    )}
                </Box>

                <FilterToolbar
                    plain
                    activeCount={(search.trim() ? 1 : 0) + (from || to ? 1 : 0)}
                    onClear={handleClearFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 320px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nº da nota, fornecedor ou chave…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    primaryIndex={0}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                        </Stack>
                    )}
                >
                    <Box>
                        <TbLabel>Período</TbLabel>
                        <DesktopPeriodSelector
                            start={toDayjs(from)}
                            end={toDayjs(to)}
                            onChange={(start, end) => {
                                setFrom(fromDayjs(start));
                                setTo(fromDayjs(end));
                            }}
                            showShortcuts={false}
                            showRefresh={false}
                            align="start"
                            dualMonth
                            clearable
                        />
                    </Box>
                </FilterToolbar>

                <FilterChips>
                    {(from || to) && (
                        <Chip
                            label={`Período: ${from ? dayjs(from).format('DD/MM/YYYY') : '…'} – ${to ? dayjs(to).format('DD/MM/YYYY') : '…'}`}
                            size="small"
                            onDelete={handleClearPeriod}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {search.trim() && (
                        <Chip
                            label={`Busca: "${search.trim()}"`}
                            size="small"
                            onDelete={() => setSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={purchases}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={openPurchase}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="compras"
                    sortableFields={['date']}
                    defaultSort={{ field: 'date', direction: 'desc' }}
                />
            </Box>

            <DialogForm
                iconType="warning"
                open={Boolean(purchaseToCancel)}
                title="Atenção"
                confirmButtonText="Sim, Cancelar compra"
                cancelButtonText="Voltar"
                content={`Tem certeza que deseja cancelar a compra${purchaseToCancel?.number ? ` da NF ${purchaseToCancel.number}` : ''}?`}
                secondaryContent="Os produtos lançados nesta compra serão retirados do estoque. Se algum já tiver sido vendido ou consumido, o cancelamento não será feito."
                onSubmit={confirmCancel}
                onClose={() => setPurchaseToCancel(null)}
                loading={canceling}
                loadingText="Cancelando..."
            />
        </PageScaffold>
    );
}
